import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useInfiniteList } from '@/hooks/useInfiniteList';

import { establecimientosApi } from '../api';
import type {
  Establecimiento,
  EstablecimientoCambiarProfesional,
  EstablecimientoCreate,
  EstablecimientoListParams,
  EstablecimientoUpdate,
  EstablecimientosByProfesionalParams,
} from '../types';

const QUERY_KEY_BASE = ['establecimientos'] as const;

export const establecimientosKeys = {
  all: QUERY_KEY_BASE,
  list: (params: EstablecimientoListParams) => [...QUERY_KEY_BASE, 'list', params] as const,
  byProfesional: (uidProfesional: string, params: EstablecimientosByProfesionalParams) =>
    [...QUERY_KEY_BASE, 'by-profesional', uidProfesional, params] as const,
  detail: (uid: string) => [...QUERY_KEY_BASE, uid] as const,
};

export function useEstablecimientos(params: EstablecimientoListParams = {}, enabled = true) {
  return useInfiniteList({
    queryKey: establecimientosKeys.list(params),
    queryFn: ({ signal, ...rest }) => establecimientosApi.list({ ...rest, signal }),
    params,
    enabled,
  });
}

export function useEstablecimientosByProfesional(
  uidProfesional: string | undefined,
  params: EstablecimientosByProfesionalParams = {}
) {
  return useInfiniteList({
    queryKey: uidProfesional
      ? establecimientosKeys.byProfesional(uidProfesional, params)
      : establecimientosKeys.all,
    queryFn: ({ signal, ...rest }) =>
      establecimientosApi.listByProfesional(uidProfesional!, { ...rest, signal }),
    params,
    enabled: !!uidProfesional,
  });
}

export function useEstablecimiento(uid: string | undefined) {
  return useQuery({
    queryKey: uid ? establecimientosKeys.detail(uid) : establecimientosKeys.all,
    queryFn: ({ signal }) => establecimientosApi.getById(uid!, signal),
    enabled: !!uid,
  });
}

export function useCreateEstablecimiento() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: EstablecimientoCreate) => establecimientosApi.create(data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: establecimientosKeys.all });
    },
  });
}

export function useUpdateEstablecimiento() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ uid, data }: { uid: string; data: EstablecimientoUpdate }) =>
      establecimientosApi.update(uid, data),
    onSuccess: (updated: Establecimiento) => {
      void queryClient.invalidateQueries({ queryKey: establecimientosKeys.all });
      queryClient.setQueryData(establecimientosKeys.detail(updated.uid_establecimiento), updated);
    },
  });
}

export function useCambiarProfesional() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ uid, data }: { uid: string; data: EstablecimientoCambiarProfesional }) =>
      establecimientosApi.cambiarProfesional(uid, data),
    onSuccess: (updated: Establecimiento) => {
      void queryClient.invalidateQueries({ queryKey: establecimientosKeys.all });
      queryClient.setQueryData(establecimientosKeys.detail(updated.uid_establecimiento), updated);
    },
  });
}

export function useDeleteEstablecimiento() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (uid: string) => establecimientosApi.remove(uid),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: establecimientosKeys.all });
    },
  });
}
